import {Button, Stack, Typography} from '@mui/material';
import React, {Fragment, useEffect, useMemo, useState} from 'react';

import {TypeLocalNameEnum} from 'domain/usecases/private/booking-flow/exams/get-exams-localities';
import {FinishWithoutCompleteFlowEnum, OdontoBookingTaggingEnum} from 'main/analytics/gaTagsEnum';
import {AlertModal, IFrameModal} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {FeaturesEnum, QueryParamsForTaggingEnum} from 'presentation/constants';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {translations} from 'presentation/translations-new';
import type {Specialty} from 'presentation/utils/categorizeSpecialtiesByFirstLetter';
import {useSearchParams} from 'react-router-dom';

import {FlowEnum, TelemedicineEnum} from '../../constants';
import {useBookingSpecialty} from '../../hooks';
import {BookingEmptyState} from '../BookingEmptyState';
import {BookingNotFoundListCard} from '../BookingNotFoundListCard';
import {CategorizedSpecialtiesList} from '../CategorizedSpecialtiesList';
import {DuplicityDialog} from '../DuplicityDialog';

import {PendingDialog} from '../PendingDialog';
import {ScriptModal} from '../ScriptModal';
import {SuggestionCard} from '../SuggestionCard';
import {TherapyAlert} from '../TherapyAlert';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {GynecologistEnum, gynecologyNames, WellBornEndpoints} from '../../constants/beBornWell';

import {CheckBookingModalEnum} from '../../hooks/useBookingSpecialty/types';

import {useWellBornEligibility} from '../../hooks/useWellBornEligibility';

import {BeBornWellCard} from '../BeBornWellCard';
import {BeBornWellDrawer} from '../BeBornWellDrawer';
import {BeBornWellNotRegister} from '../BeBornWellNotRegister';
import {BookingNotFoundVariantEnum} from '../BookingNotFoundDialog/types';
import {MostSearchedList} from '../MostSearchedList';
import {
	EligibilityFlowParams,
	NegativeProgramFlowParams,
	SpecialtyGridProps,
	SpecialtyProgramFlowParams,
} from './types';
import {ExamOrientationsDialog} from 'presentation/modules/booking/exam/ui/pages/ExamBooking/steps/ExamSelection/components/ExamOrientationsDialog';
import {MOST_SEARCHED} from 'presentation/modules/booking/shared/constants/mostSearched';
import {FilterByLetterEnum} from 'presentation/modules/booking/shared/constants/filterByLetterEnum';
import {getWellBornEligibility} from 'presentation/modules/booking/shared/services/getWellBornEligibility';
import {ExamAuthRequiredDrawer} from 'presentation/modules/booking/exam/ui/pages/ExamBooking/steps/ExamSelection/components/ExamAuthRequiredDrawer';
import {useTeaForm} from 'presentation/modules/booking/shared/hooks/useTeaForm';
import {SUGGESTED_SPECIALTY} from 'presentation/modules/booking/shared/constants/suggestedSpecialty';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {ExamEmptyState} from 'presentation/modules/booking/shared/ui/components/ExamEmptyState';
import {BookingSearchEmptyState} from 'presentation/modules/booking/shared/ui/components/BookingSearchEmptyState';
import {sortByKey} from 'presentation/utils';

const {notFoundModal, verifyingAuth, teaFlow} =
	translations['pt-br'].privateFlow.bookingFlow.specialtyFlow;
const {
	resultFound,
	loading,
	serverError,
	serverErrorTitle,
	tryAgain,
	unexpectedError,
	keep,
	close,
	notice,
	emptyDescription,
} = translations['pt-br'].commons;

export const SpecialtyGrid = ({
	filteredList,
	handleNext,
	flowType,
	allSpecialtiesAvailable,
}: SpecialtyGridProps) => {
	const {
		SEARCH_PARAMS: {query, searchByLetter},
	} = useQueryParams();
	const [searchParams] = useSearchParams();
	const {isEnabled: isExamsSpecialtyProcedureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_EXAMS_SPECIALTY_PROCEDURE,
	);
	const {isEnabled: isSemanticSearchEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SEMANTIC_SEARCH_MEDICAL_GUIDE,
	);

	const isExam = flowType === FlowEnum.EXAM;
	const isOdonto = flowType === FlowEnum.ODONTO;
	const isTeleOrHealth = flowType === FlowEnum.TELEHEALTH || flowType === FlowEnum.HEALTH;

	const allSpecialtiesAvailableFlatMap = allSpecialtiesAvailable?.flatMap(
		category => category?.list,
	);

	const hasHeadacheSpecialty = allSpecialtiesAvailableFlatMap?.some(
		specialty => specialty?.code === MOST_SEARCHED.headache.code,
	);
	const shouldShowHeadacheInMostSearched =
		isTeleOrHealth && searchByLetter === FilterByLetterEnum.N && hasHeadacheSpecialty;

	const customOrientationText = isExam
		? notFoundModal.examCardListText
		: notFoundModal.cardListText;

	const {
		beneficiary: {idadeC},
	} = useAccountDataStore(state => state.accountData);

	const [selectedSpecialty, setSelectedSpecialty] = useState<Specialty | null>(null);
	const [showBeBornWellCard, setShowBeBornWellCard] = useState(false);
	const [showBeBornWellNotRegister, setShowBeBornWellNotRegister] = useState(false);
	const [openBeBornWellDrawer, setOpenBeBornWellDrawer] = useState(false);
	const [hasProcessedParams, setHasProcessedParams] = useState(false);

	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {ufCode, cityName, typeLocalName, isManualLocation} = bookingData;
	const isUsingGeolocation = !isManualLocation;

	useEffect(() => {
		const shouldShowNotRegister =
			searchParams.get(QueryParamsForTaggingEnum.SHOW_BE_BORN_WELL_NOT_REGISTER) === 'true';

		if (shouldShowNotRegister && !hasProcessedParams) {
			const specialtyCode = searchParams.get(QueryParamsForTaggingEnum.SPECIALTY_CODE);
			const specialtyName = searchParams.get(QueryParamsForTaggingEnum.SPECIALTY_NAME);

			setShowBeBornWellNotRegister(true);

			if (specialtyCode && specialtyName && !selectedSpecialty) {
				setSelectedSpecialty({
					code: specialtyCode,
					description: specialtyName,
					formattedText: specialtyName,
				} as Specialty);
			}

			setHasProcessedParams(true);

			const currentUrl = new URL(window.location.href);
			currentUrl.searchParams.delete(
				QueryParamsForTaggingEnum.SHOW_BE_BORN_WELL_NOT_REGISTER,
			);
			window.history.replaceState({}, '', currentUrl.toString());
		} else if (hasProcessedParams && !shouldShowNotRegister) {
			setHasProcessedParams(false);
		}
	}, [searchParams, selectedSpecialty, hasProcessedParams]);

	const showEmptyState = filteredList?.every(categoryItem => categoryItem?.list?.length === 0);

	const recommendationArr = useMemo(() => {
		const allowedCategories = [
			FilterByLetterEnum.C,
			FilterByLetterEnum.O,
			FilterByLetterEnum.P,
		];
		const isPediatric = idadeC < 18;

		return filteredList
			?.filter(item => allowedCategories.includes(item?.category as FilterByLetterEnum))
			.flatMap(category => category.list)
			.filter(specialty => {
				if (isOdonto) {
					return (
						specialty?.code ===
						(isPediatric
							? SUGGESTED_SPECIALTY.generalPediatricOdonto.code
							: SUGGESTED_SPECIALTY.generalAdultOdonto.code)
					);
				}

				return (
					specialty?.code ===
					(isPediatric
						? SUGGESTED_SPECIALTY.generalPediatric.code
						: SUGGESTED_SPECIALTY.generalAdult.code)
				);
			});
	}, [filteredList]);

	const mostSearchedArr = useMemo(() => {
		const allowedCategories: FilterByLetterEnum[] = [
			FilterByLetterEnum.C,
			FilterByLetterEnum.D,
			FilterByLetterEnum.O,
			FilterByLetterEnum.P,
			FilterByLetterEnum.R,
		];

		const isPediatric = idadeC < 18;
		const filteredSpecialties = filteredList
			?.filter(item => allowedCategories.includes(item?.category as FilterByLetterEnum))
			?.flatMap(category => category?.list || []);

		const existingCodes = new Set(filteredSpecialties?.map(specialty => specialty?.code));

		const matchedSpecialties = Object.values(MOST_SEARCHED)?.filter(specialty => {
			if (isOdonto) {
				return (
					(isPediatric
						? specialty?.code === MOST_SEARCHED?.generalPediatricOdonto?.code
						: specialty?.code === MOST_SEARCHED?.generalAdultOdonto?.code) &&
					existingCodes.has(specialty?.code)
				);
			}

			return (
				(isPediatric
					? specialty?.code === MOST_SEARCHED?.generalPediatric?.code
					: specialty?.code === MOST_SEARCHED?.generalAdult?.code) &&
				existingCodes.has(specialty?.code)
			);
		});

		const additionalSpecialties = Object.values(MOST_SEARCHED)?.filter(specialty =>
			existingCodes.has(specialty?.code),
		);

		if (shouldShowHeadacheInMostSearched) {
			additionalSpecialties.push(MOST_SEARCHED.headache);
		}

		const uniqueSpecialties = Array.from(
			new Map(
				[...(matchedSpecialties || []), ...(additionalSpecialties || [])].map(item => [
					item?.code,
					item,
				]),
			).values(),
		);

		const uniqueSpecialtiesWithDetailsInfo = uniqueSpecialties?.map(specialty => {
			const foundSpecialty = allSpecialtiesAvailableFlatMap?.find(
				s => s.code === specialty.code,
			);
			return {
				...specialty,
				hasDefinition: foundSpecialty?.hasDefinition,
				telemedicine: foundSpecialty?.telemedicine ?? TelemedicineEnum.NOT_TELEMEDICINE,
			};
		});

		const listSorted = sortByKey(uniqueSpecialtiesWithDetailsInfo, 'formattedText');

		return listSorted || [];
	}, [filteredList, idadeC, flowType]);

	const {
		handleSelectCard,
		handleCheckDuplicity,
		scheduledBooking,
		checkBookingModal,
		setCheckBookingModal,
		handleChooseScriptSuggestion,
		examOrientationsModal,
		setExamOrientationsModal,
		handleAgreeExamOrientations,
		isSelectedExamAuthRequired,
		handleNavigateToExamAuth,
		examSelected,
		handleCloseExamAuthRequiredDrawer,
	} = useBookingSpecialty({
		flowType,
		handleNext,
	});

	const {
		handleCloseEmbeddedInfo,
		openEmbeddedInfo,
		getTeaFormUrl,
		IS_FETCHING,
		openIFrame,
		handleCloseIframe,
		embeddedLink,
	} = useTeaForm();

	const resetBookingDataSpecialties = () => {
		setBookingData(prev => ({
			...prev,
			specialtyCode: '',
			subSpecialtyCode: undefined,
		}));
	};

	const onCloseEmbeddedInfo = () => {
		resetBookingDataSpecialties();
		handleCloseEmbeddedInfo();
	};

	const {setEligibilityData} = useWellBornEligibility();

	const loadingText =
		checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ? verifyingAuth : loading;

	const showExamEmptyState =
		isExamsSpecialtyProcedureFlagEnabled && isSemanticSearchEnabled && isExam;

	const specialtyEmptyState = (
		<BookingEmptyState
			description={emptyDescription}
			flowType={flowType}
			dataTagId={FinishWithoutCompleteFlowEnum.SEM_ESPECIALIDADE_ENCONTRADA}
		/>
	);

	const showSuggestionCard = isOdonto ? (
		<SuggestionCard
			flowType={flowType}
			handleSelectCard={handleSelectCard}
			recommendationArr={recommendationArr}
		/>
	) : (
		<MostSearchedList
			flowType={flowType}
			handleSelectCard={handleSelectCard}
			mostSearchedArr={mostSearchedArr}
		/>
	);

	const queryInOdontoFlow = isOdonto ? (
		<Typography variant="subtitle2" color="text.secondary">
			{resultFound}
		</Typography>
	) : (
		<Fragment>
			<Typography variant="subtitle2" color="text.secondary">
				{resultFound}
			</Typography>
			<MostSearchedList
				flowType={flowType}
				handleSelectCard={handleSelectCard}
				mostSearchedArr={mostSearchedArr}
			/>
		</Fragment>
	);

	const handleSelectCardWrapper = async (specialty: Specialty) => {
		setSelectedSpecialty(specialty);

		const name = (specialty.formattedText || specialty.description || '').toUpperCase();

		if (!gynecologyNames.includes(name as GynecologistEnum)) {
			await handleSelectCard(specialty);
			return;
		}

		try {
			setCheckBookingModal('verifyingAuth');
			const eligibilityResponse = await getWellBornEligibility({
				stateCode: ufCode,
				typeLocalName: isUsingGeolocation
					? TypeLocalNameEnum.CIDADE
					: (typeLocalName ?? TypeLocalNameEnum.CIDADE),
				localName: cityName,
				specialtyCode: specialty.code.toString(),
			});
			setCheckBookingModal('none');

			if (!eligibilityResponse) {
				await handleSelectCard(specialty);
				return;
			}

			setEligibilityData(eligibilityResponse);

			const {
				cidadePrograma,
				especialidadePrograma,
				usuarioNoPrograma,
				usuarioEmCaptacao,
				usuarioNegativaPrograma,
			} = eligibilityResponse;

			if (!cidadePrograma) {
				await handleSelectCard(specialty);
				return;
			}

			handleEligibilityFlow({
				cidadePrograma,
				especialidadePrograma,
				usuarioNoPrograma,
				usuarioEmCaptacao,
				usuarioNegativaPrograma,
				specialty,
			});
		} catch {
			await handleSelectCard(specialty);
		}
	};

	const handleEligibilityFlow = ({
		cidadePrograma,
		especialidadePrograma,
		usuarioNoPrograma,
		usuarioEmCaptacao,
		usuarioNegativaPrograma,
		specialty,
	}: EligibilityFlowParams) => {
		if (usuarioNegativaPrograma) {
			handleNegativeProgramFlow({cidadePrograma, specialty});
			return;
		}

		if (especialidadePrograma) {
			handleSpecialtyProgramFlow({
				cidadePrograma,
				usuarioNoPrograma,
				usuarioEmCaptacao,
				specialty,
			});
		} else {
			handleSelectCard(specialty);
		}
	};

	const handleNegativeProgramFlow = ({cidadePrograma, specialty}: NegativeProgramFlowParams) => {
		if (cidadePrograma === WellBornEndpoints.WELL_BORN) {
			setShowBeBornWellNotRegister(true);
		} else {
			handleSelectCard(specialty);
		}
	};

	const handleSpecialtyProgramFlow = ({
		cidadePrograma,
		usuarioNoPrograma,
		usuarioEmCaptacao,
		specialty,
	}: SpecialtyProgramFlowParams) => {
		if (usuarioNoPrograma || usuarioEmCaptacao) {
			if (
				cidadePrograma === WellBornEndpoints.WELL_BORN ||
				cidadePrograma === WellBornEndpoints.CARRY_PREGNANCY_WELL
			) {
				if (cidadePrograma === WellBornEndpoints.WELL_BORN) {
					setShowBeBornWellCard(true);
				} else {
					handleSelectCard(specialty);
				}
			} else {
				handleSelectCard(specialty);
			}
		} else {
			setOpenBeBornWellDrawer(true);
		}
	};

	const handleShowBeBornWellCard = () => {
		setOpenBeBornWellDrawer(false);
		setShowBeBornWellCard(true);
	};

	const handleShowBeBornWellNotRegister = () => {
		setOpenBeBornWellDrawer(false);
		setShowBeBornWellNotRegister(true);
	};

	const handleShowBeBornWellCardFromNotRegister = () => {
		setShowBeBornWellNotRegister(false);
		setShowBeBornWellCard(true);
	};

	const handleNotPregnantClick = async () => {
		setOpenBeBornWellDrawer(false);
		const alreadyScheduled = await handleCheckDuplicity(GynecologistEnum.CODE);

		if (!alreadyScheduled) {
			const specialty: Specialty = {
				code: GynecologistEnum.CODE,
				description: GynecologistEnum.CLINICAL_GYNECOLOGY,
				formattedText: GynecologistEnum.CLINICAL_GYNECOLOGY,
				telemedicine: '',
			};
			await handleSelectCard(specialty);
		}
	};

	const handleContinueToBooking = () => {
		if (selectedSpecialty) {
			handleSelectCard(selectedSpecialty);
		}
	};

	return (
		<Fragment>
			{openBeBornWellDrawer ? (
				<BeBornWellDrawer
					open={openBeBornWellDrawer}
					setOpen={setOpenBeBornWellDrawer}
					handleNotPregnant={handleNotPregnantClick}
					handleContinueToBooking={handleContinueToBooking}
					onShowBeBornWellCard={handleShowBeBornWellCard}
					onShowBeBornWellNotRegister={handleShowBeBornWellNotRegister}
				/>
			) : null}

			{showBeBornWellCard ? (
				<BeBornWellCard flowType={flowType} continueToBooking={handleContinueToBooking} />
			) : null}

			{showBeBornWellNotRegister ? (
				<BeBornWellNotRegister
					flowType={flowType}
					selectedSpecialty={selectedSpecialty || undefined}
					handleNext={handleNext}
					onShowBeBornWellCard={handleShowBeBornWellCardFromNotRegister}
				/>
			) : null}

			<ExamAuthRequiredDrawer
				examSelected={examSelected}
				handleNavigateToExamAuth={handleNavigateToExamAuth}
				handleClose={handleCloseExamAuthRequiredDrawer}
				open={isSelectedExamAuthRequired}
			/>

			{!showBeBornWellCard && !showBeBornWellNotRegister ? (
				<Fragment>
					{showEmptyState ? (
						<Fragment>
							{showExamEmptyState ? (
								<ExamEmptyState
									dataTagId={
										FinishWithoutCompleteFlowEnum.SEM_ESPECIALIDADE_ENCONTRADA
									}
								/>
							) : isExam ? (
								specialtyEmptyState
							) : (
								<BookingSearchEmptyState
									flowType={flowType}
									dataTagId={
										FinishWithoutCompleteFlowEnum.SEM_ESPECIALIDADE_ENCONTRADA
									}>
									{specialtyEmptyState}
								</BookingSearchEmptyState>
							)}
						</Fragment>
					) : (
						<Fragment>
							{query ? queryInOdontoFlow : showSuggestionCard}
							<CategorizedSpecialtiesList
								flowType={flowType}
								handleSelectCard={handleSelectCardWrapper}
								list={filteredList}
							/>
							<Stack mt={2}>
								<BookingNotFoundListCard
									openModalDataTagId={`${OdontoBookingTaggingEnum.CLICK_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${flowType}`}
									modalHeaderModalDataTagId={`${OdontoBookingTaggingEnum.VISUALIZOU_MODAL_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${flowType}`}
									modalCloseModalDataTagId={`${OdontoBookingTaggingEnum.CLICK_FECHAR_MODAL_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${flowType}`}
									modalFirstActionDataTagId={`${OdontoBookingTaggingEnum.CLICK_CONSULTAR_REDE_ATENDIMENTO}${flowType}`}
									modalSecondActionDataTagId={`${OdontoBookingTaggingEnum.CLICK_FALAR_COM_ATENDIMENTO_ONLINE}${flowType}`}
									flowType={flowType}
									customOrientationText={customOrientationText}
									variant={BookingNotFoundVariantEnum.SPECIALTY}
								/>
							</Stack>
						</Fragment>
					)}
					{scheduledBooking ? (
						<DuplicityDialog
							flowType={flowType}
							open={checkBookingModal === CheckBookingModalEnum.DUPLICITY_STATE}
							setOpen={() => {
								resetBookingDataSpecialties();
								setCheckBookingModal('none');
							}}
							scheduledBooking={scheduledBooking}
						/>
					) : null}

					<TherapyAlert
						flowType={flowType}
						alertType="needAuth"
						open={checkBookingModal === CheckBookingModalEnum.NEED_AUTH}
						setOpen={() => {
							resetBookingDataSpecialties();
							setCheckBookingModal('none');
						}}
					/>

					<PendingDialog
						open={
							checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ||
							checkBookingModal === CheckBookingModalEnum.VERIFYING_DUPLICITY ||
							checkBookingModal === CheckBookingModalEnum.VERIFYING_DISTRICTS ||
							checkBookingModal === CheckBookingModalEnum.VERIFYING_REQUEST_SCHEDULE
						}
						text={loadingText}
					/>

					<ScriptModal
						open={checkBookingModal === CheckBookingModalEnum.SCRIPT_STATE}
						setOpen={() => {
							resetBookingDataSpecialties();
							setCheckBookingModal('none');
						}}
						handleNext={handleChooseScriptSuggestion}
					/>

					<ExamOrientationsDialog
						open={
							examOrientationsModal &&
							checkBookingModal === CheckBookingModalEnum.NONE
						}
						setOpen={setExamOrientationsModal}
						handleAgree={handleAgreeExamOrientations}
					/>

					<AlertModal
						open={checkBookingModal === CheckBookingModalEnum.ERROR}
						onClose={() => {
							resetBookingDataSpecialties();
							setCheckBookingModal('none');
						}}
						description={serverError}
						title={serverErrorTitle}
						headerTitle={unexpectedError}
						buttonLabel={tryAgain}
						variant="info"
					/>

					<AlertModal
						subDescription={
							<Typography textAlign="center">
								{teaFlow.description1} <b>{teaFlow.description2}</b>
							</Typography>
						}
						open={openEmbeddedInfo}
						onClose={onCloseEmbeddedInfo}
						headerTitle={notice}
						variant="info"
						title={teaFlow.title}
						footerContent={
							<Stack sx={{width: '100%'}} gap={1}>
								<Button
									onClick={getTeaFormUrl}
									variant="contained"
									size="large"
									fullWidth
									disabled={IS_FETCHING}
									endIcon={
										IS_FETCHING ? <Loading size={16} color="inherit" /> : null
									}>
									{keep}
								</Button>
								<Button
									onClick={onCloseEmbeddedInfo}
									variant="outlined"
									size="large"
									fullWidth>
									{close}
								</Button>
							</Stack>
						}
					/>

					<IFrameModal
						openIframe={openIFrame}
						setOpenIframe={handleCloseIframe}
						title={teaFlow.iFrameTitle}
						url={embeddedLink}
					/>
				</Fragment>
			) : null}
		</Fragment>
	);
};
